import { useEffect, useState, type ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { UserRoleRow, type MemberActions, type OrgMember } from "@/components/settings/UserRoleRow";
import { ManageSubcontractorProjectsDialog } from "@/components/settings/ManageSubcontractorProjectsDialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useOrganizationStore } from "@/hooks/useOrganizationStore";
import { useAuthStore } from "@/hooks/useAuthStore";
import { usePermissions } from "@/hooks/usePermissions";
import { useRoleLabel } from "@/hooks/useRoleLabel";
import { ROLES } from "@/constants/permissions";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

// Column headers read as small uppercase labels, matching the section labels.
const COLUMN_HEAD_CLASS = "text-xs font-medium uppercase tracking-wider text-muted-foreground";

export const UserRolesSection = observer(function UserRolesSection() {
  const { t } = useLanguage();
  const roleLabel = useRoleLabel();
  const { currentOrganization } = useOrganizationContext();
  const organizationStore = useOrganizationStore();
  const auth = useAuthStore();
  const perms = usePermissions();

  const [savingUserId, setSavingUserId] = useState<number | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<OrgMember | null>(null);
  const [memberToManageProjects, setMemberToManageProjects] = useState<OrgMember | null>(null);

  const fetchUsers = async () => {
    if (!currentOrganization) return;
    await organizationStore.fetchOrganizationUsers(currentOrganization.id);
  };

  useEffect(() => {
    if (!currentOrganization?.id) return;

    fetchUsers();
    organizationStore.subscribeToOrgChanges(currentOrganization.id);

    return () => {
      organizationStore.unsubscribeFromOrgChanges();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id]);

  const handleRoleChange = async (member: OrgMember, newRole: string) => {
    if (!currentOrganization || newRole === member.role) return;
    setSavingUserId(member.id);
    const result = await organizationStore.setMemberRole(
      currentOrganization.id,
      member.id,
      newRole
    );
    setSavingUserId(null);
    if (result.success) {
      toast.success(t.settings.roles.roleUpdated.replace("{role}", roleLabel(newRole)));
      fetchUsers();
    } else {
      toast.error(result.error ?? t.settings.roles.roleUpdateFailed);
    }
  };

  const handleRemove = async () => {
    if (!currentOrganization || !memberToRemove) return;
    const member = memberToRemove;
    setSavingUserId(member.id);
    const result = await organizationStore.removeMember(currentOrganization.id, member.id);
    setSavingUserId(null);
    setMemberToRemove(null);
    if (result.success) {
      toast.success(t.settings.roles.memberRemoved);
      fetchUsers();
    } else {
      toast.error(result.error ?? t.settings.roles.memberRemoveFailed);
    }
  };

  // Roles the actor may switch this member to (current role first, de-duplicated).
  // Empty when the row is read-only — an owner, yourself, or a tier you cannot
  // manage — in which case the role renders as a pill.
  const assignableRolesFor = (member: OrgMember): string[] => {
    const isSelf = member.id === auth.userProfileId;
    if (!perms.canManageMember(member.role) || isSelf) return [];
    const options = Array.from(new Set([member.role, ...perms.assignableMemberRoles]));
    return options.length > 1 ? options : [];
  };

  const memberActions: MemberActions = {
    onRoleChange: handleRoleChange,
    onManageProjects: setMemberToManageProjects,
    onRemove: setMemberToRemove,
  };

  const toMember = (user: OrgMember): OrgMember => ({
    id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    role: user.role,
    status: user.status,
  });

  let content: ReactNode;
  if (organizationStore.error) {
    content = (
      <div className="py-4 text-destructive">
        <p className="font-medium">{t.settings.roles.errorLoading}</p>
        <p className="text-sm">{organizationStore.error}</p>
      </div>
    );
  } else if (organizationStore.isLoading) {
    content = (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  } else if (organizationStore.users.length === 0) {
    content = <p className="text-sm text-muted-foreground">{t.common.userRolesDescription}</p>;
  } else {
    content = (
      // Flat, hairline-separated rows — the table carries no card or outer border.
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className={COLUMN_HEAD_CLASS}>{t.common.name}</TableHead>
            <TableHead className={COLUMN_HEAD_CLASS}>{t.common.email}</TableHead>
            <TableHead className={COLUMN_HEAD_CLASS}>{t.settings.roles.role}</TableHead>
            <TableHead className={COLUMN_HEAD_CLASS}>{t.settings.roles.status}</TableHead>
            {perms.canManageMembers && <TableHead className="w-[1%]" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {organizationStore.users.map((user) => {
            const member = toMember(user as OrgMember);
            const isSelf = member.id === auth.userProfileId;
            const manageable = perms.canManageMember(member.role) && !isSelf;

            return (
              <UserRoleRow
                key={member.id}
                member={member}
                perms={{
                  assignableRoles: assignableRolesFor(member),
                  // Project access is only meaningful for subcontractors (owners
                  // and admins have org-wide access). Same tier gate as remove.
                  canManageProjects: manageable && member.role === ROLES.SUBCONTRACTOR,
                  canRemove: manageable,
                  showActions: perms.canManageMembers,
                }}
                actions={memberActions}
                saving={savingUserId === member.id}
              />
            );
          })}
        </TableBody>
      </Table>
    );
  }

  return (
    <section className="space-y-4 pt-8">
      <div className="flex items-center justify-between gap-4">
        <SettingsSectionLabel>{t.common.userRoles}</SettingsSectionLabel>
        <Button
          {...pageInfoTarget("org.refreshMembers")}
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={fetchUsers}
          disabled={organizationStore.isLoading}
        >
          {organizationStore.isLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          {t.settings.roles.refresh}
        </Button>
      </div>

      {content}

      <ConfirmDialog
        open={memberToRemove !== null}
        onOpenChange={(open) => !open && setMemberToRemove(null)}
        onConfirm={handleRemove}
        title={t.settings.roles.removeMemberTitle}
        description={
          memberToRemove
            ? t.settings.roles.removeMemberDescription.replace("{email}", memberToRemove.email)
            : ""
        }
        confirmText={t.common.remove}
        cancelText={t.common.cancel}
      />

      <ManageSubcontractorProjectsDialog
        open={memberToManageProjects !== null}
        onOpenChange={(open) => !open && setMemberToManageProjects(null)}
        member={memberToManageProjects}
      />
    </section>
  );
});
